import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Search,
  UserCog,
  CheckCircle,
  Ban,
  Loader2,
  ShieldCheck,
  X,
  Mail,
  MoreHorizontal,
  ChevronUp,
  ChevronDown,
  SlidersHorizontal,
  User,
  UserPlus,
  Clock,
  Stethoscope,
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from '@/hooks/use-toast';
import { clinicAdminService, ClinicAdmin } from '@/services/clinicAdminService';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import SmartPagination from '@/components/ui/smart-pagination';
import DeactivateAdminDialog from '@/components/dialog/DeactivateAdminDialog';
import MakeAdminPractitionerSheet from '@/components/dialog/MakeAdminPractitionerSheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetClose,
} from '@/components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useClinic } from '@/contexts/ClinicContext';

// Filter options
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'archived', label: 'Archived' },
];

const addAdminSchema = z.object({
  first_name: z.string().trim().min(2, 'First name must be at least 2 characters').max(100, 'First name must be at most 100 characters'),
  last_name: z.string().trim().max(100).optional().or(z.literal('')),
  email: z.string().trim().email('Please enter a valid email').min(1, 'Email is required'),
});
type AddAdminFormData = z.infer<typeof addAdminSchema>;

const AdminManagement = () => {
  const { user } = useAuth();
  const { clinic, isAdmin } = useClinic();
  const clinicId = clinic?.id || '';

  const [admins, setAdmins] = useState<ClinicAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Filter drawer state
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Temp filter states (for drawer)
  const [tempStatusFilter, setTempStatusFilter] = useState<string>('all');
  const [tempSortBy, setTempSortBy] = useState<string>('created_at');
  const [tempSortOrder, setTempSortOrder] = useState<'asc' | 'desc'>('desc');

  // Applied filter states
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });

  const [openStatusToggleDialog, setOpenStatusToggleDialog] = useState(false);
  const [adminToToggleStatus, setAdminToToggleStatus] = useState<ClinicAdmin | null>(null);

  // Make admin practitioner state
  const [openMakePractitionerSheet, setOpenMakePractitionerSheet] = useState(false);
  const [adminToMakePractitioner, setAdminToMakePractitioner] = useState<ClinicAdmin | null>(null);

  // Add admin sheet state
  const [openAddAdminSheet, setOpenAddAdminSheet] = useState(false);
  const [isSubmittingAddAdmin, setIsSubmittingAddAdmin] = useState(false);
  const addAdminForm = useForm<AddAdminFormData>({
    resolver: zodResolver(addAdminSchema),
    defaultValues: { first_name: '', last_name: '', email: '' },
  });

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Single effect to handle all fetching
  useEffect(() => {
    if (clinicId) {
      fetchAdmins();
    }
  }, [clinicId, statusFilter, sortField, sortOrder, pagination.page, debouncedSearchTerm]);

  const fetchAdmins = async () => {
    try {
      setIsLoading(true);
      const response = await clinicAdminService.getClinicAdmins(clinicId, {
        page: pagination.page,
        limit: pagination.limit,
        search: debouncedSearchTerm,
        status: statusFilter === 'all' ? '' : statusFilter,
        sortBy: sortField,
        sortOrder: sortOrder.toUpperCase() as 'ASC' | 'DESC',
      });
      setAdmins(response.data.admins);
      if (response.data.pagination) {
        setPagination(prev => ({
          ...prev,
          totalItems: response.data.pagination.totalItems || response.data.admins.length,
          totalPages: response.data.pagination.totalPages || 1,
        }));
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to fetch admins',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return <ChevronUp className="h-3 w-3 text-slate-300" />;
    return sortOrder === 'asc' ? (
      <ChevronUp className="h-3 w-3 text-slate-700" />
    ) : (
      <ChevronDown className="h-3 w-3 text-slate-700" />
    );
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const getStatusColor = (status: string, user_status?: string) => {
    switch (status) {
      case 'active':
        if (user_status === 'invited') return 'bg-amber-500 hover:bg-amber-500 text-white border-0';
        return 'bg-emerald-600 hover:bg-emerald-600 text-white border-0';
      case 'pending':
        if (user_status === 'invited') return 'bg-amber-500 hover:bg-amber-500 text-white border-0';
        return 'bg-blue-500 hover:bg-blue-500 text-white border-0';
      case 'archived':
        return 'bg-slate-500 hover:bg-slate-500 text-white border-0';
      default:
        return 'bg-slate-100 hover:bg-slate-100 text-slate-800 border-0';
    }
  };

  const getStatusText = (status: string, user_status?: string) => {
    switch (status) {
      case 'active':
        if (user_status === 'invited') return 'Pending Acceptance';
        return 'Active';
      case 'pending':
        if (user_status === 'invited') return 'Pending Acceptance';
        return 'Pending';
      case 'archived':
        return 'Archived';
      default:
        return status;
    }
  };

  const getStatusIcon = (status: string, user_status?: string) => {
    switch (status) {
      case 'active':
        if (user_status === 'invited') return <Mail className="h-3 w-3" />;
        return <CheckCircle className="h-3 w-3" />;
      case 'pending':
        if (user_status === 'invited') return <Mail className="h-3 w-3" />;
        return <Clock className="h-3 w-3" />;
      case 'archived':
        return <Ban className="h-3 w-3" />;
      default:
        return null;
    }
  };

  // Rotate colors for user icons
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
    { bg: 'bg-teal-100', text: 'text-teal-600' },
  ];

  // Check for active filters
  const hasActiveFilters =
    statusFilter !== 'all' ||
    sortField !== 'created_at' ||
    sortOrder !== 'desc';

  const getActiveFilterCount = () => {
    let count = 0;
    if (statusFilter !== 'all') count++;
    if (sortField !== 'created_at' || sortOrder !== 'desc') count++;
    return count;
  };

  const openFilterDrawer = () => {
    setTempStatusFilter(statusFilter);
    setTempSortBy(sortField);
    setTempSortOrder(sortOrder);
    setIsFilterDrawerOpen(true);
  };

  const applyFilters = () => {
    setStatusFilter(tempStatusFilter);
    setSortField(tempSortBy);
    setSortOrder(tempSortOrder);
    setPagination(prev => ({ ...prev, page: 1 }));
    setIsFilterDrawerOpen(false);
  };

  const resetFilters = () => {
    setTempStatusFilter('all');
    setTempSortBy('created_at');
    setTempSortOrder('desc');
  };

  const clearFilter = (type: string) => {
    if (type === 'status') {
      setStatusFilter('all');
    } else if (type === 'sort') {
      setSortField('created_at');
      setSortOrder('desc');
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearAllFilters = () => {
    setStatusFilter('all');
    setSortField('created_at');
    setSortOrder('desc');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleAddAdminSubmit = async (data: AddAdminFormData) => {
    if (!clinicId) return;
    try {
      setIsSubmittingAddAdmin(true);
      await clinicAdminService.createClinicAdmin(clinicId, {
        first_name: data.first_name.trim(),
        last_name: data.last_name?.trim(),
        email: data.email.trim(),
      });
      toast({
        title: 'Success',
        description: 'Admin invited successfully. They will receive an email to set their password.',
        variant: 'default',
      });
      addAdminForm.reset();
      setOpenAddAdminSheet(false);
      fetchAdmins();
    } catch (error: any) {
      const message = error.message || error.response?.data?.error?.message || 'Failed to invite admin. Please try again.';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsSubmittingAddAdmin(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-montserrat text-slate-900 tracking-tight text-xl sm:text-2xl font-bold">
            Admin Management
          </h1>
          <p className="text-slate-500 text-sm mt-1">Manage your clinic's administrators</p>
        </div>
        {isAdmin && (
          <Button
            className="rounded-lg gap-2 bg-brand-teal hover:bg-brand-teal/90 text-white"
            onClick={() => setOpenAddAdminSheet(true)}
          >
            <UserPlus className="h-4 w-4" />
            Add Admin
          </Button>
        )}
      </div>

      {/* Filters Row */}
      <div className="flex flex-row items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Search by name or email..."
            className="pl-9 bg-white border-slate-200 rounded-lg"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value.trimStart())}
          />
        </div>

        {/* Filter Button */}
        <Button
          variant="outline"
          size="sm"
          className="h-9 border-slate-200 text-slate-700 rounded-lg ml-auto hover:bg-slate-50 hover:text-slate-700 gap-2"
          onClick={openFilterDrawer}
        >
          <SlidersHorizontal className="h-4 w-4" />
          Filter
          {getActiveFilterCount() > 0 && (
            <Badge className="ml-1 bg-red-500 text-white border-0 text-xs px-1.5 py-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
              {getActiveFilterCount()}
            </Badge>
          )}
        </Button>
      </div>

      {/* Active Filters Pills */}
      {hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-slate-500">Filters:</span>
          {statusFilter !== 'all' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Status: {STATUS_OPTIONS.find(o => o.value === statusFilter)?.label}
              <button onClick={() => clearFilter('status')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {sortField !== 'created_at' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Sort: {sortField === 'first_name' ? 'Name' : sortField === 'status' ? 'Status' : sortField}
              <button onClick={() => clearFilter('sort')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          {sortOrder !== 'desc' && sortField === 'created_at' && (
            <Badge variant="secondary" className="text-xs px-2 py-1 bg-white text-foreground hover:bg-white border border-border">
              Order: Ascending
              <button onClick={() => clearFilter('sort')} className="ml-1.5 hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </Badge>
          )}
          <button
            onClick={clearAllFilters}
            className="text-xs text-brand-teal hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
              <tr className="border-b border-slate-200">
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider w-12"
                  style={{ fontSize: '0.7rem' }}
                >
                  #
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('first_name')}
                >
                  <div className="flex items-center gap-1">
                    Name
                    <SortIcon field="first_name" />
                  </div>
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: '0.7rem' }}
                >
                  Email
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider"
                  style={{ fontSize: '0.7rem' }}
                >
                  Role
                </th>
                <th
                  className="px-4 py-3 text-left font-semibold text-slate-700 uppercase tracking-wider cursor-pointer hover:text-slate-900"
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    <SortIcon field="status" />
                  </div>
                </th>
                <th
                  className="w-12 px-2 py-3 sticky right-0"
                  style={{
                    background:
                      'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                  }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <tr key={index} className="border-b border-slate-100">
                    <td className="px-4 py-3"><Skeleton className="h-4 w-10" /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-lg" />
                        <Skeleton className="h-4 w-24" />
                      </div>
                    </td>
                    <td className="px-4 py-3"><Skeleton className="h-4 w-36" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-14 rounded-full" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-5 w-16 rounded-full" /></td>
                    <td className="px-4 py-3"><Skeleton className="h-8 w-8 rounded" /></td>
                  </tr>
                ))
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={6} className="h-48 text-center">
                    <div className="flex flex-col items-center justify-center text-slate-500">
                      <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-3">
                        <ShieldCheck className="h-7 w-7 text-slate-400" />
                      </div>
                      <p className="font-medium text-slate-600">
                        {searchTerm ? 'No admins found' : 'No admins yet'}
                      </p>
                      <p className="text-sm text-slate-400 mt-1">
                        {searchTerm ? 'Try adjusting your search' : 'No administrators in this clinic'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                admins.map((admin, index) => {
                  const color = colors[index % colors.length];
                  return (
                    <tr
                      key={admin.id}
                      className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                    >
                      <td
                        className="px-4 py-3 text-slate-500 whitespace-nowrap"
                        style={{ fontSize: '0.8rem' }}
                      >
                        #{String((pagination.page - 1) * pagination.limit + index + 1).padStart(3, '0')}
                      </td>
                      <td className="px-4 py-3 max-w-[200px] whitespace-nowrap" style={{ fontSize: '0.8rem' }}>
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 ${color.bg} rounded-lg flex items-center justify-center flex-shrink-0`}
                          >
                            <User className={`h-4 w-4 ${color.text}`} />
                          </div>
                          <div className="min-w-0">
                            <span className="font-medium text-slate-900 truncate block max-w-[150px] capitalize">
                              {admin.first_name} {admin.last_name}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 max-w-[180px]" style={{ fontSize: '0.8rem' }}>
                        <span className="block truncate" title={admin.email || '--'}>
                          {admin.email || '--'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" style={{ fontSize: '0.8rem' }}>
                        <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          Admin
                        </Badge>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Badge
                          className={`${getStatusColor(admin.status, admin.user?.status)} text-xs inline-flex items-center gap-1 w-fit`}
                        >
                          {getStatusIcon(admin.status, admin.user?.status)}
                          <span className="capitalize">{getStatusText(admin.status, admin.user?.status)}</span>
                        </Badge>
                      </td>
                      <td
                        className="w-12 px-2 py-3 sticky right-0"
                        style={{
                          background:
                            'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
                        }}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-slate-100">
                              <MoreHorizontal className="h-4 w-4 text-slate-500" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuItem
                              onClick={() => {
                                setAdminToToggleStatus(admin);
                                setOpenStatusToggleDialog(true);
                              }}
                              className={`cursor-pointer ${admin.status === 'active' ? 'text-red-600' : 'text-emerald-600'}`}
                              style={{ fontSize: '13px' }}
                              disabled={admin.user_id === user?.id}
                            >
                              {admin.status === 'active' ? (
                                <>
                                  <Ban className="h-4 w-4 mr-2" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Activate
                                </>
                              )}
                            </DropdownMenuItem>
                            {admin.status === 'active'
                              && (admin.user?.status ?? admin.user_status) !== 'invited'
                              && !admin.is_practitioner && (
                                <DropdownMenuItem
                                  onClick={() => {
                                    setAdminToMakePractitioner(admin);
                                    setOpenMakePractitionerSheet(true);
                                  }}
                                  className="cursor-pointer text-blue-600"
                                  style={{ fontSize: '13px' }}
                                >
                                  <Stethoscope className="h-4 w-4 mr-2" />
                                  Make Practitioner
                                </DropdownMenuItem>
                              )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="border-t border-slate-200 px-2 sm:px-4 py-3 flex flex-col sm:flex-row items-center sm:justify-between gap-3">
            <div className="w-full sm:w-auto overflow-x-auto">
              <SmartPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                className="mx-0 justify-center sm:justify-start"
                maxVisiblePages={3}
              />
            </div>
            <p className="text-xs text-slate-500 whitespace-nowrap">
              Total: <span className="font-medium text-slate-700">{pagination.totalItems}</span> Admins
            </p>
          </div>
        )}
      </div>

      {/* Admin Status Toggle Confirmation Dialog */}
      <DeactivateAdminDialog
        open={openStatusToggleDialog}
        onOpenChange={setOpenStatusToggleDialog}
        admin={adminToToggleStatus}
        onSuccess={() => {
          setOpenStatusToggleDialog(false);
          fetchAdmins();
        }}
      />

      {/* Make Admin Practitioner Sheet */}
      <MakeAdminPractitionerSheet
        open={openMakePractitionerSheet}
        onOpenChange={setOpenMakePractitionerSheet}
        clinicId={clinicId}
        admin={adminToMakePractitioner ? {
          id: adminToMakePractitioner.id,
          user_id: adminToMakePractitioner.user_id,
          first_name: adminToMakePractitioner.user?.first_name || adminToMakePractitioner.first_name || '',
          last_name: adminToMakePractitioner.user?.last_name || adminToMakePractitioner.last_name || '',
          email: adminToMakePractitioner.user?.email || adminToMakePractitioner.email || '',
        } : null}
        onSuccess={() => {
          setOpenMakePractitionerSheet(false);
          fetchAdmins();
        }}
      />

      {/* Add Admin Dialog */}
      <Dialog open={openAddAdminSheet} onOpenChange={setOpenAddAdminSheet}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Add Admin</DialogTitle>
          </DialogHeader>
          <Form {...addAdminForm}>
            <form onSubmit={addAdminForm.handleSubmit(handleAddAdminSubmit)} className="space-y-4">
              <FormField
                control={addAdminForm.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="required">First name</FormLabel>
                    <FormControl>
                      <Input placeholder="First name" className="rounded-lg" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={addAdminForm.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name (optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Last name" className="rounded-lg" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={addAdminForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="required">Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="email@example.com" className="rounded-lg" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="gap-2 pt-2 sm:pt-4">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-lg"
                  onClick={() => setOpenAddAdminSheet(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="rounded-lg bg-brand-teal hover:bg-brand-teal/90 text-white"
                  disabled={isSubmittingAddAdmin}
                >
                  {isSubmittingAddAdmin ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Inviting…
                    </>
                  ) : (
                    'Invite Admin'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Filter Drawer */}
      <Sheet open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
        <SheetContent
          className="w-full max-w-[400px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden"
          style={{ minWidth: 'auto' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
            <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
              Filter & Sort
            </SheetTitle>
            <SheetClose asChild>
              <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
                <X className="h-5 w-5 text-slate-600" />
              </button>
            </SheetClose>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
            {/* Status Filter */}
            <div className="space-y-1">
              <Label className="font-medium" style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Status
              </Label>
              <Select value={tempStatusFilter} onValueChange={setTempStatusFilter}>
                <SelectTrigger className="w-full h-10 border-slate-200 focus:ring-0 focus:border-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors duration-200">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUS_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort By */}
            <div className="space-y-1">
              <Label className="font-medium" style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Sort By
              </Label>
              <Select value={tempSortBy} onValueChange={setTempSortBy}>
                <SelectTrigger className="w-full h-10 border-slate-200 focus:ring-0 focus:border-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors duration-200">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Date Created</SelectItem>
                  <SelectItem value="first_name">Name</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sort Order */}
            <div className="space-y-1">
              <Label className="font-medium" style={{ fontSize: '0.75rem', color: '#0f172a' }}>
                Sort Order
              </Label>
              <Select value={tempSortOrder} onValueChange={value => setTempSortOrder(value as 'asc' | 'desc')}>
                <SelectTrigger className="w-full h-10 border-slate-200 focus:ring-0 focus:border-black focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none transition-colors duration-200">
                  <SelectValue placeholder="Select order" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">Ascending</SelectItem>
                  <SelectItem value="desc">Descending</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
            <Button
              variant="ghost"
              className="text-slate-700 hover:text-slate-900 hover:bg-transparent font-medium"
              onClick={resetFilters}
            >
              Reset all
            </Button>
            <Button
              className="bg-brand-teal hover:bg-brand-teal/90 rounded-lg h-11 px-8"
              onClick={applyFilters}
            >
              Apply Filter
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminManagement;
